import React from 'react';
import {
  Users, BookOpen, DollarSign, Calendar, Trash2, GraduationCap,
  Briefcase, UserPlus, CheckCircle, CalendarDays, ChevronRight,
  AlertTriangle, Award, Zap, Target, BarChart3, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Legend
} from 'recharts';

const AdminOverview = ({
  user, dashboardData, analyticsData, teachers, students,
  teacherIssues, approvalData, resettingData,
  handleResetData, setActiveSection,
  createUserOpen, setCreateUserOpen, handleCreateUser,
  newUser, setNewUser, isCreating,
}) => {
  return (
    <>
      <div className="ds-page-hero flex items-start justify-between" data-testid="admin-page-hero">
        <div>
          <h1 data-testid="admin-overview-title">Dashboard Overview</h1>
          <p>Welcome back, {user?.name}</p>
        </div>
        <Button onClick={handleResetData} disabled={resettingData} variant="outline" size="sm" className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20 hover:text-red-300 font-bold" data-testid="reset-data-btn">
          <Trash2 className="w-4 h-4 mr-2" />{resettingData ? 'Resetting...' : 'Reset Data'}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { label: 'Total Users', value: dashboardData.users?.total || 0, icon: Users, gradient: 'from-blue-600 to-blue-500', shadow: 'shadow-blue-600/20' },
          { label: 'Students', value: dashboardData.users?.students || 0, icon: GraduationCap, gradient: 'from-emerald-600 to-emerald-500', shadow: 'shadow-emerald-600/20' },
          { label: 'Teachers', value: dashboardData.users?.teachers || 0, icon: Briefcase, gradient: 'from-violet-600 to-violet-500', shadow: 'shadow-violet-600/20' },
          { label: 'Courses', value: dashboardData.courses?.total || 0, icon: BookOpen, gradient: 'from-cyan-600 to-cyan-500', shadow: 'shadow-cyan-600/20' },
          { label: 'Revenue', value: `₹${(dashboardData.revenue?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, gradient: 'from-amber-600 to-amber-500', shadow: 'shadow-amber-600/20' },
          { label: 'Bookings', value: dashboardData.bookings?.total || 0, icon: Calendar, gradient: 'from-rose-600 to-rose-500', shadow: 'shadow-rose-600/20' },
        ].map((kpi, i) => (
          <div key={kpi.label} className={`bg-gradient-to-br ${kpi.gradient} rounded-2xl p-4 shadow-lg ${kpi.shadow} hover:scale-[1.03] transition-all cursor-default`} data-testid={`kpi-${kpi.label.toLowerCase().replace(/ /g,'-')}`}>
            <kpi.icon className="w-7 h-7 text-white/80 mb-2" />
            <div className="text-2xl font-extrabold text-slate-900">{kpi.value}</div>
            <div className="text-xs text-white/70 font-semibold">{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Approvals & Issues Summary Widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <button onClick={() => setActiveSection('approvals')} className="glass-card rounded-2xl p-5 text-left hover:shadow-lg hover:scale-[1.02] transition-all group" data-testid="widget-approvals">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-orange-400" /></div>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-900 transition-colors" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{approvalData.pending_count || 0}</p>
          <p className="text-sm text-slate-500 font-semibold">Pending Approvals</p>
        </button>
        <button onClick={() => setActiveSection('teacher-issues')} className="glass-card rounded-2xl p-5 text-left hover:shadow-lg hover:scale-[1.02] transition-all group" data-testid="widget-issues">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-900 transition-colors" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{teacherIssues.length || 0}</p>
          <p className="text-sm text-slate-500 font-semibold">Teacher Issues</p>
        </button>
        <button onClick={() => setActiveSection('teachers')} className="glass-card rounded-2xl p-5 text-left hover:shadow-lg hover:scale-[1.02] transition-all group" data-testid="widget-teachers">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center"><Briefcase className="w-5 h-5 text-violet-400" /></div>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-900 transition-colors" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{teachers.length}</p>
          <p className="text-sm text-slate-500 font-semibold">Active Teachers</p>
        </button>
        <button onClick={() => setActiveSection('students')} className="glass-card rounded-2xl p-5 text-left hover:shadow-lg hover:scale-[1.02] transition-all group" data-testid="widget-students">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-emerald-400" /></div>
            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-slate-900 transition-colors" />
          </div>
          <p className="text-2xl font-bold text-slate-900">{students.length}</p>
          <p className="text-sm text-slate-500 font-semibold">Active Students</p>
        </button>
      </div>

      {/* Charts Row 1: Revenue + Class Completion */}
      {analyticsData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="glass-card rounded-2xl p-5 shadow-sm" data-testid="chart-revenue">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-emerald-400" />
              <h3 className="font-bold text-slate-900 text-base">Monthly Revenue (INR)</h3>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={analyticsData.revenue_months}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', color: '#0F172A' }} />
                <Bar dataKey="revenue" fill="url(#revGradient)" radius={[6, 6, 0, 0]} />
                <defs>
                  <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="glass-card rounded-2xl p-5 shadow-sm" data-testid="chart-class-completion">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-blue-400" />
              <h3 className="font-bold text-slate-900 text-base">Class Completion Analytics</h3>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <RePieChart>
                <Pie data={analyticsData.class_completion.filter(c => c.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={3} label={({ name, value }) => `${name}: ${value}`}>
                  {analyticsData.class_completion.filter(c => c.value > 0).map((entry) => (
                    <Cell key={entry.name} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', color: '#0F172A' }} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Charts Row 2: Curriculum Progress */}
      {analyticsData && analyticsData.curriculum_progress.length > 0 && (
        <div className="glass-card rounded-2xl p-5 shadow-sm mb-8" data-testid="chart-curriculum">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-indigo-400" />
            <h3 className="font-bold text-slate-900 text-base">Curriculum Progress</h3>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(200, analyticsData.curriculum_progress.length * 50)}>
            <BarChart data={analyticsData.curriculum_progress} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 12 }} unit="%" />
              <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 11 }} width={180} />
              <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', color: '#0F172A' }} formatter={(val) => `${val}%`} />
              <Bar dataKey="pct" fill="url(#currGradient)" radius={[0, 6, 6, 0]} barSize={20} />
              <defs>
                <linearGradient id="currGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#6366F1" />
                  <stop offset="100%" stopColor="#818CF8" />
                </linearGradient>
              </defs>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Performance Section */}
      {analyticsData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="glass-card rounded-2xl p-5 shadow-sm" data-testid="perf-teachers">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-amber-400" />
              <h3 className="font-bold text-slate-900 text-base">Top Performing Teachers</h3>
            </div>
            {analyticsData.top_teachers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No teacher data yet</p>
            ) : (
              <div className="space-y-3">
                {analyticsData.top_teachers.map((t, i) => (
                  <div key={t.email || i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-slate-400 text-white' : 'bg-amber-700 text-white'}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{t.classes} classes</p>
                      <p className="text-xs text-emerald-400">{t.completed} completed</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="glass-card rounded-2xl p-5 shadow-sm" data-testid="perf-students">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-cyan-400" />
              <h3 className="font-bold text-slate-900 text-base">Most Active Students</h3>
            </div>
            {analyticsData.top_students.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No student data yet</p>
            ) : (
              <div className="space-y-3">
                {analyticsData.top_students.map((s, i) => (
                  <div key={s.id || i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-cyan-500 text-white' : i === 1 ? 'bg-slate-400 text-white' : 'bg-cyan-700 text-white'}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 truncate">{s.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{s.classes} classes</p>
                      <p className="text-xs text-emerald-400">{s.completed} completed</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
          <DialogTrigger asChild>
            <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl p-5 text-white cursor-pointer hover:shadow-xl hover:scale-[1.03] transition-all shadow-lg shadow-blue-600/20" data-testid="action-add-user">
              <UserPlus className="w-8 h-8 mb-3 text-white/80" />
              <h3 className="text-base font-bold">Add New User</h3>
              <p className="text-blue-200 text-xs mt-1">Create teacher, student, or parent</p>
            </div>
          </DialogTrigger>
          <DialogContent className="bg-white border border-blue-200 shadow-xl">
            <DialogHeader>
              <DialogTitle className="text-slate-900 font-bold">Create New User</DialogTitle>
              <DialogDescription>Add a new user to the platform</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-900 font-bold">Full Name</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required className="mt-1 border-2 border-gray-300 focus:border-blue-500 text-black" />
              </div>
              <div>
                <Label className="text-slate-900 font-bold">Email</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required className="mt-1 border-2 border-gray-300 focus:border-blue-500 text-black" />
              </div>
              <div>
                <Label className="text-slate-900 font-bold">Username</Label>
                <Input value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} placeholder="Login username" className="mt-1 border-2 border-gray-300 focus:border-blue-500 text-black" />
              </div>
              <div>
                <Label className="text-slate-900 font-bold">Password</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required className="mt-1 border-2 border-gray-300 focus:border-blue-500 text-black" />
              </div>
              <div>
                <Label className="text-slate-900 font-bold">Role</Label>
                <Select value={newUser.role} onValueChange={(val) => setNewUser({...newUser, role: val})}>
                  <SelectTrigger className="mt-1 border-2 border-gray-300 text-black"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="teacher">Teacher</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-black font-bold" disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create User'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        <div onClick={() => setActiveSection('curriculum')} className="bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-2xl p-5 text-white cursor-pointer hover:shadow-xl hover:scale-[1.03] transition-all shadow-lg shadow-emerald-600/20" data-testid="action-manage-courses">
          <BookOpen className="w-8 h-8 mb-3 text-white/80" />
          <h3 className="text-base font-bold">Manage Courses</h3>
          <p className="text-emerald-200 text-xs mt-1">Curriculum management</p>
        </div>
        <div onClick={() => setActiveSection('demos')} className="bg-gradient-to-br from-violet-600 to-violet-500 rounded-2xl p-5 text-white cursor-pointer hover:shadow-xl hover:scale-[1.03] transition-all shadow-lg shadow-violet-600/20" data-testid="action-demo-bookings">
          <CalendarDays className="w-8 h-8 mb-3 text-white/80" />
          <h3 className="text-base font-bold">Demo Bookings</h3>
          <p className="text-violet-200 text-xs mt-1">Manage demo sessions</p>
        </div>
      </div>
    </>
  );
};

export default AdminOverview;
